import React, { useState, useEffect } from 'react';
import { 
  getGenderColor 
} from '../data/voiceOptions';
import { 
  loadAvailableVoices, 
  savePreferredVoice, 
  getPreferredVoice 
} from '../utils/voiceManager';
import { voiceQualityOptimizer } from '../utils/voiceQualityOptimizer';

const VoiceSelector = ({ isOpen, onClose, onVoiceChange }) => {
  const [availableVoices, setAvailableVoices] = useState([]);
  const [selectedVoiceId, setSelectedVoiceId] = useState('');
  const [previewingVoiceId, setPreviewingVoiceId] = useState(null);
  const [deviceInfo, setDeviceInfo] = useState({});
  const [optimizationInfo, setOptimizationInfo] = useState({});
  const [debugInfo, setDebugInfo] = useState({});
  const [activeTab, setActiveTab] = useState('voices');
  const [voiceSettings, setVoiceSettings] = useState({
    speechRate: 0.8,
    pitch: 1.0,
    volume: 0.9,
    autoPlay: false,
    voiceQuality: 'high'
  });

  useEffect(() => {
    if (isOpen) {
      // Load available voices
      loadAvailableVoices((voices) => {
        console.log('Available voices loaded:', voices);
        setAvailableVoices(voices);
        
        // Set currently selected voice
        const currentVoiceId = getPreferredVoice();
        if (currentVoiceId) {
          setSelectedVoiceId(currentVoiceId);
        } else {
          // Default to first available voice
          setSelectedVoiceId(voices[0]?.id || '');
        }

        // Debug info
        const debugData = {
          totalVoices: voices.length,
          availableVoices: voices.map(v => ({
            id: v.id,
            name: v.fallbackVoice?.name || 'No voice',
            lang: v.fallbackVoice?.lang || 'Unknown',
            default: v.fallbackVoice?.default || false
          })),
          selectedVoice: currentVoiceId,
          localStorageValue: localStorage.getItem('preferredTutorVoice')
        };
        setDebugInfo(debugData);
      });

      // Get device and optimization information
      const deviceInfo = {
        isMobileSafari: voiceQualityOptimizer.detectMobileSafari(),
        isMobile: voiceQualityOptimizer.detectMobile(),
        userAgent: navigator.userAgent
      };
      setDeviceInfo(deviceInfo);

      const optimizationInfo = {
        level: voiceQualityOptimizer.determineOptimizationLevel(),
        recommendations: voiceQualityOptimizer.getOptimizationRecommendations()
      };
      setOptimizationInfo(optimizationInfo);

      // Load saved voice settings
      const savedSettings = localStorage.getItem('voiceSettings');
      if (savedSettings) {
        setVoiceSettings(JSON.parse(savedSettings));
      }
    }
  }, [isOpen]);

  const handleVoiceSelect = (voiceId) => {
    console.log('Voice selected:', voiceId);
    setSelectedVoiceId(voiceId);
  };

  const handlePreviewVoice = (voiceOption) => {
    if (previewingVoiceId === voiceOption.id) {
      // Stop preview
      window.speechSynthesis.cancel();
      setPreviewingVoiceId(null);
      return;
    }

    // Start preview
    setPreviewingVoiceId(voiceOption.id);
    
    const utterance = new SpeechSynthesisUtterance("Hello! I'm your English tutor. How can I help you today?");
    utterance.voice = voiceOption.fallbackVoice;
    utterance.rate = voiceSettings.speechRate;
    utterance.pitch = voiceSettings.pitch;
    utterance.volume = voiceSettings.volume;
    
    utterance.onstart = () => {
      console.log('Preview started for voice:', voiceOption.fallbackVoice?.name);
    };

    utterance.onend = () => {
      setPreviewingVoiceId(null);
      console.log('Preview ended for voice:', voiceOption.fallbackVoice?.name);
    };

    utterance.onerror = () => {
      setPreviewingVoiceId(null);
      alert('Voice preview failed. This voice may not be available on your device.');
    };

    window.speechSynthesis.speak(utterance);
  };

  const handleSaveVoice = () => {
    if (selectedVoiceId) {
      console.log('Saving voice selection:', selectedVoiceId);
      savePreferredVoice(selectedVoiceId);
      
      // Save voice settings
      localStorage.setItem('voiceSettings', JSON.stringify(voiceSettings));
      
      onVoiceChange(selectedVoiceId);
      
      // Show confirmation
      const selectedVoice = availableVoices.find(v => v.id === selectedVoiceId);
      if (selectedVoice) {
        alert(`Voice settings saved! You selected: ${selectedVoice.accent} (${selectedVoice.gender}) - ${selectedVoice.fallbackVoice?.name || 'Unknown voice'}`);
      }
      
      onClose();
    }
  };

  const handleSettingChange = (setting, value) => {
    setVoiceSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  const getVoiceQualityIndicator = (voiceOption) => {
    if (!voiceOption.fallbackVoice) {
      return <span className="text-red-400 text-xs">Not Available</span>;
    }

    const device = deviceInfo.isMobileSafari ? 'Mobile Safari' : 
                   deviceInfo.isMobile ? 'Mobile' : 'Desktop';
    
    // Get optimization level for this device
    const optimizationLevel = optimizationInfo.level;
    
    let qualityClass = '';
    let qualityText = '';
    
    if (device === 'Mobile Safari') {
      if (optimizationLevel === 'high') {
        qualityClass = 'text-green-400';
        qualityText = 'Optimized';
      } else if (optimizationLevel === 'medium') {
        qualityClass = 'text-yellow-400';
        qualityText = 'Good';
      } else {
        qualityClass = 'text-orange-400';
        qualityText = 'Basic';
      }
    } else if (device === 'Mobile') {
      if (optimizationLevel === 'high') {
        qualityClass = 'text-green-400';
        qualityText = 'Optimized';
      } else {
        qualityClass = 'text-yellow-400';
        qualityText = 'Good';
      }
    } else {
      qualityClass = 'text-green-400';
      qualityText = 'Excellent';
    }
    
    return (
      <span className={`${qualityClass} text-xs font-medium`}>
        {qualityText}
      </span>
    );
  };

  const getVoiceCardStyle = (voiceId) => {
    if (voiceId === selectedVoiceId) {
      return 'bg-purple-600 border-purple-500 shadow-lg';
    }
    return 'bg-gray-800 border-gray-700 hover:border-gray-600';
  };

  const groupVoicesByCategory = () => {
    const groups = {};
    availableVoices.forEach(voice => {
      const category = voice.accent;
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(voice);
    });
    return groups;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-lg shadow-2xl w-full max-w-2xl h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700 flex-shrink-0">
          <h2 className="text-xl font-bold text-white">Voice Settings</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Device Info */}
        <div className="px-6 py-3 bg-gray-800 border-b border-gray-700 flex-shrink-0">
          <div className="text-xs text-gray-400">
            <div className="flex items-center gap-2">
              <span>Device:</span>
              <span className={`px-2 py-1 rounded ${
                deviceInfo.isMobileSafari ? 'bg-orange-600 text-white' :
                deviceInfo.isMobile ? 'bg-blue-600 text-white' :
                'bg-green-600 text-white'
              }`}>
                {deviceInfo.isMobileSafari ? 'Mobile Safari' :
                 deviceInfo.isMobile ? 'Mobile' : 'Desktop'}
              </span>
            </div>
            <div className="mt-1">
              <span>Optimization:</span>
              <span className={`ml-2 px-2 py-1 rounded ${
                optimizationInfo.level === 'high' ? 'bg-green-600 text-white' :
                optimizationInfo.level === 'medium' ? 'bg-yellow-600 text-white' :
                'bg-orange-600 text-white'
              }`}>
                {optimizationInfo.level === 'high' ? 'High' :
                 optimizationInfo.level === 'medium' ? 'Medium' : 'Low'}
              </span>
            </div>
            {/* Debug Info */}
            <div className="mt-2 text-xs">
              <span>Available Voices: {debugInfo.totalVoices || 0}</span>
              <br />
              <span>Selected: {debugInfo.selectedVoice || 'None'}</span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-700 flex-shrink-0">
          <button
            onClick={() => setActiveTab('voices')}
            className={`px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'voices'
                ? 'text-white border-b-2 border-blue-500'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Voice Selection
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'settings'
                ? 'text-white border-b-2 border-blue-500'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Voice Settings
          </button>
          <button
            onClick={() => setActiveTab('test')}
            className={`px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'test'
                ? 'text-white border-b-2 border-blue-500'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Test & Preview
          </button>
        </div>

        {/* Tab Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 min-h-0">
          {activeTab === 'voices' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white mb-4">Choose Your Tutor Voice</h3>
              {Object.entries(groupVoicesByCategory()).map(([category, voices]) => (
                <div key={category} className="space-y-3">
                  <h4 className="text-md font-medium text-gray-300 border-b border-gray-600 pb-2">
                    {category}
                  </h4>
                  {voices.map((voiceOption) => (
                    <div
                      key={voiceOption.id}
                      className={`border-2 rounded-lg p-4 transition-all duration-200 cursor-pointer ${getVoiceCardStyle(voiceOption.id)}`}
                      onClick={() => handleVoiceSelect(voiceOption.id)}
                    >
                      {/* Voice Header */}
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <span className="text-gray-400 font-mono text-sm">
                            {voiceOption.countryCode}
                          </span>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-white font-semibold">
                                {voiceOption.accent} ({voiceOption.gender})
                              </span>
                              <span className={`text-lg ${getGenderColor(voiceOption.gender)}`}>
                                {voiceOption.genderIcon}
                              </span>
                            </div>
                            <p className="text-gray-300 text-sm mt-1">
                              {voiceOption.characteristics}
                            </p>
                            {/* Debug: Show actual voice name */}
                            {voiceOption.fallbackVoice && (
                              <p className="text-blue-300 text-xs mt-1">
                                Voice: {voiceOption.fallbackVoice.name}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        {/* Preview Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePreviewVoice(voiceOption);
                          }}
                          className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                            previewingVoiceId === voiceOption.id
                              ? 'bg-red-500 hover:bg-red-600'
                              : 'bg-blue-500 hover:bg-blue-600'
                          }`}
                        >
                          {previewingVoiceId === voiceOption.id ? (
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M6 4h12v16H6z"/>
                            </svg>
                          ) : (
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z"/>
                            </svg>
                          )}
                        </button>
                      </div>

                      {/* Voice Quality Indicator */}
                      <div className="flex items-center justify-between">
                        {getVoiceQualityIndicator(voiceOption)}
                        
                        {/* Selection Indicator */}
                        {voiceOption.id === selectedVoiceId && (
                          <div className="flex items-center gap-2 text-purple-400 text-sm">
                            <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                            <span>已選擇</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-white mb-4">Voice Preferences</h3>
              
              {/* Speech Rate */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Speech Rate: {voiceSettings.speechRate}
                </label>
                <input
                  type="range"
                  min="0.5"
                  max="2.0"
                  step="0.1"
                  value={voiceSettings.speechRate}
                  onChange={(e) => handleSettingChange('speechRate', parseFloat(e.target.value))}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>Slow</span>
                  <span>Normal</span>
                  <span>Fast</span>
                </div>
              </div>

              {/* Pitch */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Pitch: {voiceSettings.pitch}
                </label>
                <input
                  type="range"
                  min="0.5"
                  max="2.0"
                  step="0.1"
                  value={voiceSettings.pitch}
                  onChange={(e) => handleSettingChange('pitch', parseFloat(e.target.value))}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>Low</span>
                  <span>Normal</span>
                  <span>High</span>
                </div>
              </div>

              {/* Volume */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Volume: {voiceSettings.volume}
                </label>
                <input
                  type="range"
                  min="0.0"
                  max="1.0"
                  step="0.1"
                  value={voiceSettings.volume}
                  onChange={(e) => handleSettingChange('volume', parseFloat(e.target.value))}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>Quiet</span>
                  <span>Normal</span>
                  <span>Loud</span>
                </div>
              </div>

              {/* Voice Quality */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Voice Quality Priority
                </label>
                <select
                  value={voiceSettings.voiceQuality}
                  onChange={(e) => handleSettingChange('voiceQuality', e.target.value)}
                  className="w-full p-2 bg-gray-800 border border-gray-600 rounded-lg text-white"
                >
                  <option value="high">High Quality (Slower)</option>
                  <option value="medium">Balanced</option>
                  <option value="low">Fast (Lower Quality)</option>
                </select>
              </div>

              {/* Auto-play Setting */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="autoPlay"
                  checked={voiceSettings.autoPlay}
                  onChange={(e) => handleSettingChange('autoPlay', e.target.checked)}
                  className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                />
                <label htmlFor="autoPlay" className="ml-2 text-sm text-gray-300">
                  Auto-play voice when text is displayed
                </label>
              </div>
            </div>
          )}

          {activeTab === 'test' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-white mb-4">Test Your Voice Settings</h3>
              
              {/* Test Text Input */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Test Text
                </label>
                <textarea
                  defaultValue="Hello! This is a test of your selected voice and settings. How does it sound?"
                  className="w-full p-3 bg-gray-800 border border-gray-600 rounded-lg text-white resize-none"
                  rows="3"
                />
              </div>

              {/* Test Controls */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    if (selectedVoiceId) {
                      const selectedVoice = availableVoices.find(v => v.id === selectedVoiceId);
                      if (selectedVoice && selectedVoice.fallbackVoice) {
                        console.log('Testing selected voice:', selectedVoice.fallbackVoice.name);
                        const utterance = new SpeechSynthesisUtterance("Hello! This is a test of your selected voice. How does it sound?");
                        utterance.voice = selectedVoice.fallbackVoice;
                        utterance.rate = voiceSettings.speechRate;
                        utterance.pitch = voiceSettings.pitch;
                        utterance.volume = voiceSettings.volume;
                        window.speechSynthesis.speak(utterance);
                      }
                    }
                  }}
                  disabled={!selectedVoiceId}
                  className="flex-1 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                >
                  Test Current Settings
                </button>
                <button
                  onClick={() => window.speechSynthesis.cancel()}
                  className="px-6 py-3 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-lg transition-colors"
                >
                  Stop
                </button>
              </div>

              {/* Voice Information */}
              {selectedVoiceId && (
                <div className="bg-gray-800 p-4 rounded-lg">
                  <h4 className="text-md font-medium text-white mb-2">Current Voice Information</h4>
                  {(() => {
                    const selectedVoice = availableVoices.find(v => v.id === selectedVoiceId);
                    if (selectedVoice && selectedVoice.fallbackVoice) {
                      return (
                        <div className="text-sm text-gray-300 space-y-1">
                          <p><strong>Name:</strong> {selectedVoice.fallbackVoice.name}</p>
                          <p><strong>Language:</strong> {selectedVoice.fallbackVoice.lang}</p>
                          <p><strong>Default:</strong> {selectedVoice.fallbackVoice.default ? 'Yes' : 'No'}</p>
                          <p><strong>Accent:</strong> {selectedVoice.accent} ({selectedVoice.gender})</p>
                          <p><strong>Characteristics:</strong> {selectedVoice.characteristics}</p>
                        </div>
                      );
                    }
                    return <p className="text-red-400">No voice selected</p>;
                  })()}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer - Fixed at bottom */}
        <div className="p-6 border-t border-gray-700 flex-shrink-0">
          <div className="flex gap-3">
            <button
              onClick={() => {
                if (selectedVoiceId) {
                  const selectedVoice = availableVoices.find(v => v.id === selectedVoiceId);
                  if (selectedVoice && selectedVoice.fallbackVoice) {
                    console.log('Testing selected voice:', selectedVoice.fallbackVoice.name);
                    const utterance = new SpeechSynthesisUtterance("This is a test of your selected voice. How does it sound?");
                    utterance.voice = selectedVoice.fallbackVoice;
                    utterance.rate = voiceSettings.speechRate;
                    utterance.pitch = voiceSettings.pitch;
                    utterance.volume = voiceSettings.volume;
                    window.speechSynthesis.speak(utterance);
                  }
                }
              }}
              disabled={!selectedVoiceId}
              className="flex-1 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition-colors"
            >
              Test Voice
            </button>
            <button
              onClick={handleSaveVoice}
              disabled={!selectedVoiceId}
              className="flex-1 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition-colors"
            >
              Save Settings
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoiceSelector;
